import { Panel, TreeView, TreeViewItem, Label } from '@playcanvas/pcui/react';
import React from 'react';

import { SetProperty, ObserverData } from '../../types';

interface FileNode {
    name: string;
    path: string;
    isFile: boolean;
    size?: number;
    children?: FileNode[];
}

const bytesToSizeString = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (i === 0) ? `${bytes} ${sizes[i]}` : `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
};

class FilesPanel extends React.Component<{ filesData: ObserverData['files'], setProperty: SetProperty }> {
    shouldComponentUpdate(nextProps: Readonly<{ filesData: ObserverData['files']; setProperty: SetProperty; }>): boolean {
        return JSON.stringify(nextProps.filesData) !== JSON.stringify(this.props.filesData);
    }

    // Build folder hierarchy from flat file list
    private buildFileTree(files: Array<{filename: string, url: string, size?: number}>): FileNode[] {
        const root: FileNode[] = [];
        const folderMap = new Map<string, FileNode>();

        files.forEach(file => {
            const pathParts = file.filename.split('/');
            let currentLevel: FileNode[] = root;
            let currentPath = '';

            // Process each folder in the path
            for (let i = 0; i < pathParts.length - 1; i++) {
                const folderName = pathParts[i];
                currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;
                
                let folder = currentLevel.find(item => item.name === folderName && !item.isFile);
                if (!folder) {
                    folder = {
                        name: folderName,
                        path: currentPath,
                        isFile: false,
                        children: []
                    };
                    currentLevel.push(folder);
                    folderMap.set(currentPath, folder);
                }
                currentLevel = folder.children!;
            }

            // Add the file
            const fileName = pathParts[pathParts.length - 1];
            currentLevel.push({
                name: fileName,
                path: file.filename,
                isFile: true,
                size: file.size
            });
        });

        return root;
    }

    private renderFileTree(nodes: FileNode[]): React.ReactNode[] {
        return nodes.map((node) => {
            if (node.isFile) {
                return (
                    <TreeViewItem 
                        key={node.path} 
                        text={node.name}
                        icon={node.name.toLowerCase().endsWith('.gltf') || node.name.toLowerCase().endsWith('.glb') ? 'E149' : 
                              node.name.toLowerCase().endsWith('.png') || node.name.toLowerCase().endsWith('.jpg') || node.name.toLowerCase().endsWith('.jpeg') ? 'E121' :
                              'E184'}
                        allowSelect={false}
                    >
                        {node.size && (
                            <Label class='file-size' text={bytesToSizeString(node.size)} />
                        )}
                    </TreeViewItem>
                );
            } else {
                return (
                    <TreeViewItem 
                        key={node.path} 
                        text={node.name}
                        icon='E259'
                        allowSelect={false}
                    >
                        {this.renderFileTree(node.children || [])}
                    </TreeViewItem>
                );
            }
        });
    }

    render() {
        const filesData = this.props.filesData;
        const files = filesData?.list || [];
        
        if (files.length === 0) {
            return (
                <Panel headerText='FILES' class='files-panel' collapsible={true}>
                    <Label class='empty-state' text='No files loaded' />
                </Panel>
            );
        }

        const fileTree = this.buildFileTree(files);
        const totalFiles = files.length;
        const totalSize = files.reduce((sum: number, file: {filename: string, url: string, size?: number}) => sum + (file.size || 0), 0);

        return (
            <Panel headerText='FILES' class='files-panel' collapsible={true}>
                <div className='files-summary'>
                    <Label text={`${totalFiles} files`} />
                    {totalSize > 0 && <Label text={bytesToSizeString(totalSize)} />}
                </div>
                <TreeView allowReordering={false} allowDrag={false}>
                    {this.renderFileTree(fileTree)}
                </TreeView>
            </Panel>
        );
    }
}

export default FilesPanel; 